import i18n from "@/shared/lang/i18n";
import { PIX_KEY_META, pixKeyValid, resolvePixKeyType } from "@/shared/utils/pixKey";

import {
  getPaymentMethodFields,
  isBinanceUidEmailExclusivePair,
} from "../constants/paymentMethodFields.config";
import type { PaymentMethodFields } from "../types/new-payment.types";

const REQUIRED = "Este campo es obligatorio.";
const ERR_UID_DIGITS_ONLY = "El UID solo admite números.";
const ERR_EMAIL_INVALID = "Introduce un email válido.";
const ERR_BINANCE_ONE_REQUIRED = "Indica UID o email (solo uno).";
const ERR_BINANCE_NOT_BOTH = "Indica solo UID o solo email, no ambos.";

const ERR_PIX_KEY_TYPE_REQUIRED = "Selecciona el tipo de llave Pix.";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isPixCurrencyMethod(currency: string, method: string): boolean {
  return currency.trim() === "BRL" && method.trim() === "Pix";
}

// Pix key rules live in `@/shared/utils/pixKey` (shared with the dispersions recipient panels). The check
// is mask-tolerant: the input now stores the masked display value (CPF "000.000.000-00", etc.) and the
// payload build normalizes it back to the raw key before persisting (`buildCreateClientAccountPayload`).
function validatePixFields(methodFields: PaymentMethodFields): Partial<Record<string, string>> {
  const errors: Partial<Record<string, string>> = {};
  const keyType = resolvePixKeyType(methodFields.pix_type);
  const key = (methodFields.pix_key ?? "").trim();

  if (!keyType) errors.pix_type = ERR_PIX_KEY_TYPE_REQUIRED;
  if (!key) errors.pix_key = REQUIRED;
  if (!keyType || !key) return errors;

  if (!pixKeyValid(keyType, key)) {
    // `PIX_KEY_META` carries global-namespace i18n keys; this util is not a hook, so it translates
    // through the i18n singleton (validation re-runs pick up any language change).
    errors.pix_key = i18n.t(`global:${PIX_KEY_META[keyType].err}`);
  }
  return errors;
}

function validateBinanceUidEmailExclusive(
  methodFields: PaymentMethodFields,
): Partial<Record<string, string>> {
  const uid = (methodFields.binancePayUid ?? "").trim();
  const email = (methodFields.binancePayEmail ?? "").trim();
  const errors: Partial<Record<string, string>> = {};
  if (uid && email) {
    errors.binancePayUid = ERR_BINANCE_NOT_BOTH;
    errors.binancePayEmail = ERR_BINANCE_NOT_BOTH;
    return errors;
  }
  if (!uid && !email) {
    errors.binancePayUid = ERR_BINANCE_ONE_REQUIRED;
    return errors;
  }
  if (uid && !/^\d+$/.test(uid)) {
    errors.binancePayUid = ERR_UID_DIGITS_ONLY;
  }
  if (email && !EMAIL_RE.test(email)) {
    errors.binancePayEmail = ERR_EMAIL_INVALID;
  }
  return errors;
}

export function validatePaymentMethodFields(
  currency: string,
  method: string,
  methodFields: PaymentMethodFields,
): Partial<Record<string, string>> {
  if (isBinanceUidEmailExclusivePair(currency, method)) {
    return validateBinanceUidEmailExclusive(methodFields);
  }
  if (isPixCurrencyMethod(currency, method)) {
    return validatePixFields(methodFields);
  }

  const fields = getPaymentMethodFields(currency, method);
  const errors: Partial<Record<string, string>> = {};
  for (const field of fields) {
    const raw = methodFields[field.id];
    const str = raw == null ? "" : String(raw).trim();
    if (field.required !== false && str === "") {
      errors[field.id] = REQUIRED;
      continue;
    }
    if (str === "") continue;

    if (field.id === "binancePayUid") {
      if (!/^\d+$/.test(str)) {
        errors[field.id] = ERR_UID_DIGITS_ONLY;
      }
      continue;
    }
    if (field.id === "binancePayEmail") {
      if (!EMAIL_RE.test(str)) {
        errors[field.id] = ERR_EMAIL_INVALID;
      }
    }
  }
  return errors;
}
